import { useMutation } from '@tanstack/react-query';
import { executeQuery } from '../api/analyticsApi';
import { useAnalyticsStore } from '../stores/analyticsStore';
import type { ListResult } from '../types/analytics.types';

export function usePatientDrilldown() {
  const {
    currentQuery,
    setUIState,
    setQueryResult,
    setPatientListPage,
    clearPatientSelection,
  } = useAnalyticsStore();

  return useMutation({
    mutationFn: async ({ page = 1, pageSize = 25 }: { page?: number; pageSize?: number } = {}) => {
      if (!currentQuery) {
        throw new Error('No query to drill down from');
      }

      const listQuery = {
        ...currentQuery,
        output: { type: 'list' as const, limit: pageSize },
      };

      const response = await executeQuery(listQuery, page, pageSize);
      return response;
    },
    onMutate: () => {
      setUIState('loading');
      clearPatientSelection();
    },
    onSuccess: (data, variables) => {
      setQueryResult(data.result);
      setPatientListPage(variables?.page || 1);
      setUIState('patient_list');
    },
    onError: () => {
      setUIState('results');
    },
  });
}

export function usePatientListFromResult() {
  const { queryResult } = useAnalyticsStore();

  if (!queryResult || queryResult.data.type !== 'list') {
    return {
      patients: [],
      totalCount: 0,
      page: 1,
      pageSize: 25,
    };
  }

  const listData = queryResult.data as ListResult;
  return {
    patients: listData.patients,
    totalCount: listData.totalCount,
    page: listData.page,
    pageSize: listData.pageSize,
  };
}
